/**
 * ProductsByCategorySection — Airbnb-style home product layout.
 * Shows products grouped by category, each in a horizontal row.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import CategorySection from './CategorySection';
import ProductSkeleton from './ProductSkeleton';
import { useProductsByCategory } from '@/hooks/useProductsByCategory';

const MAX_VISIBLE_PER_CATEGORY = 10;

export default function ProductsByCategorySection() {
  const { groups, loading, error, refetch } = useProductsByCategory();

  const totalProducts = useMemo(
    () => groups.reduce((sum, g) => sum + g.total, 0),
    [groups],
  );

  return (
    <section className="pt-8 pb-10 bg-white" id="productos">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Section header — solo desktop */}
        <div className="hidden md:flex items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#4bbfdf' }}>Catálogo</span>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mt-1">
              Nuestros productos
            </h2>
            {!loading && totalProducts > 0 && (
              <p className="text-sm text-stone-500 mt-1">
                {totalProducts} productos en {groups.length}{' '}
                {groups.length === 1 ? 'categoría' : 'categorías'}
              </p>
            )}
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-1.5 text-sm font-semibold cursor-pointer whitespace-nowrap shrink-0 transition-colors"
            style={{ color: '#4bbfdf' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#2aa8cc'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#4bbfdf'; }}
          >
            Ver todo
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-2xl">
              <i className="ri-error-warning-line text-red-500 text-xl" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-stone-800 mb-1 text-sm">
                Error al cargar productos
              </p>
              <p className="text-xs text-stone-500 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                style={{ background: '#4bbfdf' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#2aa8cc'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#4bbfdf'; }}
              >
                <i className="ri-refresh-line" />
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="space-y-12">
            {Array.from({ length: 3 }).map((_, gi) => (
              <div key={gi}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-gray-200 rounded-xl animate-pulse" />
                  <div className="space-y-1.5">
                    <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
                <div className="flex gap-3 overflow-hidden">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="shrink-0 w-[220px] md:flex-1">
                      <ProductSkeleton />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Category sections */}
        {!loading && !error && groups.length > 0 && (
          <div>
            {groups.map((group) => (
              <CategorySection
                key={group.categoryId}
                categoryId={group.categoryId}
                categoryName={group.categoryName}
                products={group.products}
                totalProducts={group.total}
                maxVisible={MAX_VISIBLE_PER_CATEGORY}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && groups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-2xl">
              <i className="ri-inbox-line text-stone-400 text-2xl" />
            </div>
            <p className="text-sm text-stone-500">No hay productos disponibles</p>
          </div>
        )}
      </div>
    </section>
  );
}
